import Box from '@mui/material/Box';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhMitreEndpointItem, WazuhMitreTechniqueItem } from 'src/types/mindtime/wazuhMitre';
import type { Column } from 'src/types/table';
import {
  buildDetectionDetailsRows,
  buildDetectionEvidenceRows,
  collectMitreIds,
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrMitreDocsLinks,
  EdrSeverityPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  formatEdrDateTime,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  getWindowsEventMessage,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
} from '../shared/edrPrimitives';

import { toEdrSeverityLevel, EDR_SEVERITY_FILTER_OPTIONS } from '../shared/edrFormat';

export const mitreSeverityToLevel = toEdrSeverityLevel;
export const MITRE_SEVERITY_FILTER_OPTIONS = EDR_SEVERITY_FILTER_OPTIONS;

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toMitreTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};


/**
 * Route/API id for technique detail.
 * Prefers resource `id`/`_id`; falls back to `mitreId` when list rows only expose the technique id.
 */
export const getMitreTechniqueId = (
  item: Pick<WazuhMitreTechniqueItem, 'id' | '_id' | 'mitreId'> | null | undefined
): string => String(item?.id ?? item?._id ?? item?.mitreId ?? '').trim();


const formatMitreListNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

type MitreListColumnScope = 'global' | 'endpoint';

export const getMitreTechniqueListColumns = (scope: MitreListColumnScope): Column[] => {
  const isEndpoint = scope === 'endpoint';

  return [
    {
      id: 'mitreId',
      label: 'MITRE ID',
      render: (row) => (
        <span style={{ ...edrMonoStyle, fontWeight: 600, color: EDR_COLORS.brand600 }}>
          {(row as unknown as WazuhMitreTechniqueItem).mitreId || '—'}
        </span>
      ),
    },
    {
      id: 'technique',
      label: 'Technique',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhMitreTechniqueItem).techniqueName || '—'}
          style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}
          maxWidth={280}
        />
      ),
    },
    {
      id: 'tactic',
      label: 'Tactic',
      render: (row) => (
        <span style={{ color: EDR_COLORS.textSecondary }}>
          {(row as unknown as WazuhMitreTechniqueItem).tactic || '—'}
        </span>
      ),
    },
    {
      id: 'detections',
      label: 'Detections',
      render: (row) => (
        <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
          {formatMitreListNumber((row as unknown as WazuhMitreTechniqueItem).detections)}
        </span>
      ),
    },
    ...(isEndpoint
      ? []
      : [
          {
            id: 'endpoint',
            label: 'Affected Endpoint',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhMitreTechniqueItem).endpoint || '—'}
                style={edrMonoStyle}
                maxWidth={160}
              />
            ),
          },
          {
            id: 'hostName',
            label: 'Endpoint Label',
            render: (row) => (
              <EdrTruncatedText
                value={formatEndpointLabel((row as unknown as WazuhMitreTechniqueItem).hostName)}
                style={edrMutedStyle}
                maxWidth={160}
              />
            ),
          },
        ]),
    {
      id: 'severity',
      label: 'Severity',
      render: (row) => {
        const item = row as unknown as WazuhMitreTechniqueItem;
        return (
          <EdrSeverityPill
            level={mitreSeverityToLevel(item.severity)}
            label={toMitreTitle(item.severity) || '—'}
          />
        );
      },
    },
    {
      id: 'timestamp',
      label: 'Timestamp',
      render: (row) => (
        <EdrDateTime value={(row as unknown as WazuhMitreTechniqueItem).timestamp} />
      ),
    },
  ];
};

const formatMitreEndpointCount = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const mitreSeverityCountCell = (value: number | undefined | null, criticalTint = false) => {
  const count = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return (
    <span
      style={{
        fontWeight: 600,
        color: criticalTint && count > 0 ? EDR_COLORS.critical : EDR_COLORS.textPrimary,
      }}
    >
      {formatMitreEndpointCount(count)}
    </span>
  );
};

export const getMitreAffectedEndpointListColumns = (): Column[] => [
  {
    id: 'systemWazuhAgentId',
    label: 'Endpoint',
    sortable: true,
    render: (row) => {
      const item = row as unknown as WazuhMitreEndpointItem;
      const canOpen = Boolean(item.systemWazuhAgentId?.trim());
      return (
        <span style={edrEndpointNameLinkStyle(canOpen)}>
          {item.endpoint ?? item.name ?? item.systemWazuhAgentId ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'hostName',
    label: 'Endpoint Label',
    render: (row) => (
      <span style={edrMutedStyle}>
        {formatEndpointLabel((row as unknown as WazuhMitreEndpointItem).hostName)}
      </span>
    ),
  },
  {
    id: 'activeTechniques',
    label: 'Active Techniques',
    align: 'center',
    sortable: true,
    render: (row) => (
      <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {formatMitreEndpointCount((row as unknown as WazuhMitreEndpointItem).activeTechniques)}
      </span>
    ),
  },
  {
    id: 'highRiskTechniques',
    label: 'High Risk',
    align: 'center',
    sortable: true,
    render: (row) =>
      mitreSeverityCountCell(
        (row as unknown as WazuhMitreEndpointItem).highRiskTechniques,
        true
      ),
  },
  {
    id: 'totalDetections',
    label: 'Total Detections',
    align: 'center',
    sortable: true,
    render: (row) => (
      <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {formatMitreEndpointCount((row as unknown as WazuhMitreEndpointItem).totalDetections)}
      </span>
    ),
  },
  {
    id: 'critical',
    label: 'Critical',
    align: 'center',
    sortable: true,
    render: (row) =>
      mitreSeverityCountCell(
        (row as unknown as WazuhMitreEndpointItem).detectionsBySeverity?.critical,
        true
      ),
  },
  {
    id: 'high',
    label: 'High',
    align: 'center',
    sortable: true,
    render: (row) =>
      mitreSeverityCountCell(
        (row as unknown as WazuhMitreEndpointItem).detectionsBySeverity?.high
      ),
  },
  {
    id: 'medium',
    label: 'Medium',
    align: 'center',
    sortable: true,
    render: (row) =>
      mitreSeverityCountCell(
        (row as unknown as WazuhMitreEndpointItem).detectionsBySeverity?.medium
      ),
  },
  {
    id: 'low',
    label: 'Low',
    align: 'center',
    sortable: true,
    render: (row) =>
      mitreSeverityCountCell(
        (row as unknown as WazuhMitreEndpointItem).detectionsBySeverity?.low
      ),
  },
];

export const buildMitreDetailProps = (
  item: WazuhMitreTechniqueItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = mitreSeverityToLevel(item.severity);
  const severityLabel = toMitreTitle(item.severity) || '—';
  const mitreIds = collectMitreIds(item.mitreId, item.mitreIds);
  const organization = item.organizationName?.trim() || item.organization?.trim() || '—';
  const tactics =
    item.tactics?.map((t) => t?.trim()).filter(Boolean) ??
    (item.tactic?.trim() ? [item.tactic.trim()] : []);
  const techniqueLabel = item.techniqueName?.trim() || item.techniqueNames?.[0]?.trim() || '—';
  const description = item.detection?.trim() || '';
  const message = getWindowsEventMessage(item.data);
  const evidenceRows = buildDetectionEvidenceRows(item);
  const detectionId = String(item.id ?? item._id ?? '').trim();

  return {
    backLink: {
      label: 'Back to MITRE ATT&CK',
      to: getSecurityNavItemPath('mitre-attck') ?? '',
    },
    title: item.mitreId?.trim() || 'Technique',
    badges: <EdrSeverityPill level={severityLevel} label={severityLabel} />,
    metaLine: (
      <>
        {techniqueLabel}
        {' · Detected '}
        {formatEdrDateTime(item.timestamp)}
        {' on '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint?.trim() || '—'}</strong>
      </>
    ),
    main: (
      <>
        {description || message ? (
          <EdrDetailSectionCard
            icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
            title="Description"
          >
            {description ? <EdrExpandableText text={description} lines={3} /> : null}
            {message ? (
              <Box style={{ marginTop: description ? 12 : 0 }}>
                <EdrExpandableText text={message} lines={4} />
              </Box>
            ) : null}
          </EdrDetailSectionCard>
        ) : null}

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Technique">
          <EdrDetailKeyValueRows
            rows={[
              { label: 'Technique', value: techniqueLabel },
              {
                label: 'MITRE ID',
                value: mitreIds.length ? mitreIds.join(', ') : '—',
                mono: true,
              },
              { label: 'Tactic', value: tactics.length ? tactics.join(', ') : '—' },
              { label: 'Threat Category', value: item.threatCategory?.trim() || '—' },
              { label: 'Severity', value: severityLabel },
              { label: 'Detection Time', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        {evidenceRows.length > 0 ? (
          <EdrDetailSectionCard
            icon={<SearchIcon style={{ fontSize: 16 }} />}
            title="Detection Evidence"
          >
            <EdrDetailKeyValueRows rows={evidenceRows} />
          </EdrDetailSectionCard>
        ) : null}

        <EdrDetailSectionCard icon={<LinkIcon style={{ fontSize: 16 }} />} title="MITRE References">
          <EdrMitreDocsLinks ids={mitreIds} />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <>
        <EdrDetailEndpointSidebar
          endpoint={item.endpoint?.trim() || '—'}
          hostName={item.hostName}
          organization={organization}
          operatingSystem={item.os?.trim() || undefined}
        />
        <EdrDetailSectionCard
          icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
          title="Detection Details"
        >
          <EdrDetailKeyValueRows rows={buildDetectionDetailsRows(item, detectionId)} />
        </EdrDetailSectionCard>
      </>
    ),
  };
};
